import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { contar } from '@fiodecobre/shared';
import { atendimentoApi } from '../modules/atendimento/lib/api';
import { ModalMensagensNaoEntregues } from '../modules/atendimento/modais/ModalMensagensNaoEntregues';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Saude {
  sem_confirmacao: number;
  total: number;
  contatos: number;
  desde: string | null;
  pct: number;
  janela_h: number;
}

const POLLING_MS = 3 * 60 * 1000;

// Ambar escuro solido + texto preto, o mesmo do PERDIDO — e o vizinho semantico certo: os dois
// dizem "o cliente ficou sem o que a loja mandou". Vermelho fica pro que PARA a loja (Caixa), e
// aqui o sistema segue de pe. Daltonismo-safe (nunca verde/vermelho pra distinguir estado).
const FUNDO = '#b45309';
const BORDA = '#92400e';
const TEXTO = '#000';

// Portao do alarme — calibrado no dado medido em 11/08/2026 (DA-503), nao chutado.
// Saudavel: 1,27% das mensagens do ERP ficam sem recibo (20 presas em 1.574, 20/07 a 08/08),
// o que da 1 a 5 por DIA. Durante o buraco do Baileys: 42% a 53%. Os dois portoes juntos
// nunca teriam disparado no periodo saudavel e teriam pego a falha na primeira manha.
// ⚠️ Exigir os DOIS de proposito: so a taxa dispara em janela de volume baixo (1 de 4 = 25%),
// e so a contagem dispara num dia movimentado com clientes de celular desligado.
const MIN_MENSAGENS = 4;
const MIN_PCT = 15;

// Saude do canal de saida do WhatsApp (DA-502/DA-503). Alarme de CANAL, nao de mensagem:
// a mediana do recibo e 3,4s, mas 14% das entregas BOAS so confirmam depois de 5min e 4%
// depois de 1h (celular desligado que volta) — marcar mensagem a mensagem acertaria ~1 em 4
// e viraria alarme que ninguem le (DA-410). O que discrimina e a TAXA.
//
// A faixa some sozinha quando os recibos voltam: nada aqui e gravado, tudo e derivado da
// janela movel — nao ha sinalizador pra alguem lembrar de limpar (licao da DA-458).
//
// Clicar abre o painel "Mensagens nao entregues" (DA-504), que e quem LISTA. A faixa nao
// expande mais inline: duas listas da mesma coisa seriam duas coisas pra manter em sincronia,
// e a faixa so existe enquanto o canal esta ruim — a porta permanente do painel e a engrenagem.
export function AlertaEntregaWhatsAppBanner() {
  const [saude, setSaude] = useState<Saude | null>(null);
  const [painelAberto, setPainelAberto] = useState(false);

  useEffect(() => {
    let cancelado = false;
    async function tick() {
      try {
        const r = await atendimentoApi.saudeEntrega();
        if (!cancelado) setSaude(r);
      } catch { /* silencia: alerta que quebra a tela e pior que alerta ausente */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, []);

  const ativo = !!saude && saude.sem_confirmacao >= MIN_MENSAGENS && saude.pct >= MIN_PCT;
  const recolhido = useAlertaFaixa(
    'entrega-whatsapp',
    ativo ? { rotulo: 'WhatsApp', bg: FUNDO, borda: BORDA, qtd: saude!.sem_confirmacao } : null,
  );

  // O painel segue montado mesmo com a faixa recolhida/apagada: se ela sumir enquanto ele esta
  // aberto (os recibos voltaram no meio da leitura), fechar na cara de quem esta lendo seria
  // arrancar a tela. Quem fecha e o operador.
  const painel = <ModalMensagensNaoEntregues aberto={painelAberto} onFechar={() => setPainelAberto(false)} />;

  if (!ativo || recolhido) return painel;
  const s = saude!;

  return (
    <>
      <div style={{ background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: TEXTO, fontSize: 13 }}>
        <button
          type="button"
          onClick={() => setPainelAberto(true)}
          title="Ver quais mensagens nao foram confirmadas"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', width: '100%',
            background: 'transparent', border: 'none', color: TEXTO, font: 'inherit',
            textAlign: 'left', cursor: 'pointer',
          }}
        >
          <span style={{ fontWeight: 700, letterSpacing: 0.3, flexShrink: 0 }}>WHATSAPP</span>
          <span>
            {contar(s.sem_confirmacao, 'mensagem')} sem confirmacao de entrega
            {s.desde ? ` desde ${s.desde}` : ''} — <b>{s.pct}%</b> das ultimas {s.janela_h}h, em {contar(s.contatos, 'contato')}.
            O cliente pode nao ter recebido: confirme por outro caminho antes de reenviar.
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 700, flexShrink: 0, textDecoration: 'underline' }}>
            Ver quais <ChevronRight size={15} />
          </span>
        </button>
      </div>
      {painel}
    </>
  );
}
